import { useState, useCallback, useMemo } from 'react';
import { FeatureLike } from 'ol/Feature';
import { MapPresenter } from '../MapPresenter';
import { MapWidgetPosition } from '../components/widgets/MapWidget.types';
import { MapSettingsService } from '../services/MapSettingsService';
import { UseShareStateReturn } from './useShareState';
import type { AlertPoint } from '../../efas/notifications/Notification.types';

export type NotificationFilters = {
    partner: string;
    dissPartner: string;
    type: string;
};

function applyFilters(points: AlertPoint[], filters: NotificationFilters): AlertPoint[] {
    let result = points;
    if (filters.partner !== "All") {
        result = result.filter(n => n.partner_abbr.map(p => p.toString()).includes(filters.partner));
    }
    if (filters.dissPartner !== "All") {
        result = result.filter(n => n.diss_partner === filters.dissPartner);
    }
    if (filters.type === "Formal") {
        result = result.filter(n => n.alert_type === 1);
    } else if (filters.type === "Informal") {
        result = result.filter(n => n.alert_type === 2);
    } else if (filters.type === "Flash") {
        result = result.filter(n => n.alert_type === 3);
    }
    return result;
}

const DEFAULT_FILTERS: NotificationFilters = { partner: "All", dissPartner: "All", type: "All" };

/// Handles the state of all interactive layers' features but was primarily
/// written for the Active Notifications widget, hence it handling things like show and hide
export function useInteractivePopup(
    presenter: MapPresenter,
    remote: MapSettingsService,
    share: UseShareStateReturn
) {

    // All points from the API (unfiltered)
    const [allPoints, setAllPoints] = useState<AlertPoint[]>([]);
    const [filters, setFilters] = useState<NotificationFilters>(DEFAULT_FILTERS);
    const [position, setPosition] = useState<MapWidgetPosition>(
        MapWidgetPosition.HIDDEN
    );
    const [feature, setFeature] = useState<FeatureLike | undefined>(undefined);

    const filteredPoints = useMemo(() => applyFilters(allPoints, filters), [allPoints, filters]);

    const updateFilter = useCallback((key: keyof NotificationFilters, value: string) => {
        setFilters(prev => {
            const next = { ...prev, [key]: value };
            // Re-render map icons with the new filter applied
            const filtered = applyFilters(allPoints, next);
            presenter.renderNotificationIcons(filtered);
            return next;
        });
    }, [allPoints, presenter]);

    const setPoints = useCallback((points: AlertPoint[]) => {
        setAllPoints(points);
        // Apply current filters to new data and render on map
        const filtered = applyFilters(points, filters);
        presenter.renderNotificationIcons(filtered);
    }, [filters, presenter]);

    const show = useCallback(async (forecastDate: string) => {
        console.debug("Show active notifications clicked");
        let newState: string = "off";
        if (position === MapWidgetPosition.HIDDEN) {
            const active = await presenter.showActiveNotifications(forecastDate);
            setAllPoints(active);
            setFilters(DEFAULT_FILTERS);
            setPosition(MapWidgetPosition.LEFT);
            newState = "on";
        } else {
            presenter.clearActiveNotifications();
            setAllPoints([]);
            setPosition(MapWidgetPosition.HIDDEN);
        }
        share.updateActiveNotif(newState)
        remote.updateUserWebSettings({ activenotif: newState });
    }, [position, presenter, remote, share]);

    const closePopup = useCallback(() => {
        presenter.clearInteractivePopup();
        setFeature(undefined);
    }, [presenter]);

    return {
        allPoints,
        filteredPoints,
        filters,
        updateFilter,
        position,
        feature,
        show,
        setPosition,
        setPoints,
        setFeature,
        closePopup,
    };
}
